import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";
import {
  IconEdit,
  IconEyeSearch,
  IconPlus,
  IconRefreshAlert,
  IconTrash,
} from "@tabler/icons-react";

import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { deleteAdminOrder, getAdminOrders } from "api/order.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import {
  confirmDelete,
  formatDate,
  getOrderStageLabel,
  truncate,
} from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import Reprint from "pages/admin/components/Reprint";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useProductStore } from "stores/useProductStore";
import { InvoiceExportSummary, PaginatedOrderDetails } from "types/model.types";
import AdminExportInvoicePage from "./AdminExportInvoicePage";
import AdminOrderEntryPage from "./AdminOrderEntryPage";
import AdminOrderPreview from "./AdminOrderPreview";

const AdminOrderPage: React.FC = () => {
  const apiFetchRef = React.useRef<boolean>(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [recordId, setRecordId] = useState<number | null>(null);
  const [orderStage, setOrderStage] = useState<number>(0);
  const [preview, setPreview] = useState(false);
  const [entries, setEntries] = useState<PaginatedOrderDetails>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { isLoading } = useProductStore();
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);
  const [exportData, setExportData] = useState<InvoiceExportSummary | null>(
    null,
  );

  useEffect(() => {
    if (apiFetchRef.current) return; // if already fetched, do not refetch
    apiFetchRef.current = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        await safeApiCall(getAdminOrders(activePage, DEFAULT_PAGE_SIZE)).then(
          (data) => {
            apiFetchRef.current = false;

            if (data) setEntries(data);
            setLoading(false);
          },
        );
      } catch (err) {
        apiFetchRef.current = false;
        logError(err);
        setLoading(false);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      }
    };

    fetchData();
  }, [activePage, reloadTrigger]);

  useEffect(() => {
    if (!id) return;

    handleEdit(id);
    setTimeout(() => {
      navigate("/admin/orders", { replace: true });
    }, 500);
  }, [id]);

  const handlePrint = (id: string | number, orderStage: number) => {
    setRecordId(Number(id));
    setOrderStage(orderStage);
    navigateTo(`/print/orders?id=${id}&s=${orderStage}&e=0`);
  };
  const handleEdit = async (id: string | number) => {
    // trigger product download in case user tries to edit an order before visiting any product related page, as we need the product list to show the product details in the order entry page. This is a temporary workaround until we have a more robust solution for product data management in the app.
    useProductStore.getState().forceRefresh();
    setRecordId(Number(id));
    setTimeout(() => {
      setEntryModalOpen(true);
    }, 500);
  };

  const handleDelete = async (id: string | number) => {
    if (!id) return;
    if (confirmDelete() === false) return;
    try {
      await safeApiCall(deleteAdminOrder(Number(id)));
      setReloadTrigger(new Date());
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
    } catch {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  const rows = entries.data.map((item) => {
    return (
      <Table.Tr
        key={item.id}
        title={`Note1: ${item.notes ?? ""} Note2: ${item.notes2 ?? ""},`}
      >
        <Table.Td className="action-cell action-cell-lg">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Print">
              <ActionIcon
                variant="subtle"
                onClick={() => {
                  handlePrint(item.id, item.orderStage);
                }}
              >
                <IconEyeSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={item.isProcessed ? "Edit (disabled)" : "Edit"}>
              <ActionIcon
                variant="subtle"
                color="blue"
                disabled={item.isProcessed}
                onClick={() => handleEdit(item.id)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={item.isProcessed ? "Delete (disabled)" : "Delete"}>
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={item.isProcessed}
                onClick={() => handleDelete(item.id)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{item.id}</Table.Td>
        <Table.Td>{formatDate(item.createdOn)}</Table.Td>
        <Table.Td>{getOrderStageLabel(item.orderStage)}</Table.Td>
        <Table.Td>{item.invoiceToDebtorId}</Table.Td>
        <Table.Td>{item.deliverToDebtorId}</Table.Td>
        <Table.Td>{item.yourOrderNo}</Table.Td>
        <Table.Td>{item.salesPerson}</Table.Td>
        <Table.Td className="notes">
          {truncate(item.notes ?? "", item.notes2, 55)}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack>
      <Title order={2}>Orders</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Group justify="flex-start" wrap="nowrap" mb="md">
              <Reprint
                isOrderPage={true}
                onReprint={(
                  id: number,
                  orderStage?: number | string | undefined,
                ) => {
                  navigateTo(`/print/orders?id=${id}&s=${orderStage}&e=1`);
                }}
              />
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setRecordId(null);
                  setEntryModalOpen(true);
                }}
              >
                Add Order
              </Button>
              <Button
                leftSection={<IconRefreshAlert size={16} />}
                title="This will flush the product list in memory and force system to re-fetch from database."
                onClick={() => {
                  useProductStore.getState().forceRefresh();
                  notifications.show({
                    message: "Product list refreshed...",
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Success,
                  });
                }}
              >
                Force Refresh
              </Button>
            </Group>

            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Order #</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Order Stage</Table.Th>
                <Table.Th>Invoice To</Table.Th>
                <Table.Th>Deliver To</Table.Th>
                <Table.Th>Customer Order Ref</Table.Th>
                <Table.Th>Sales Person</Table.Th>
                <Table.Th className="notes">Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
      <Modal
        fullScreen
        opened={entryModalOpen}
        onClose={() => {
          setEntryModalOpen(false);
          setReloadTrigger(new Date());
        }}
        title={"Add/Edit Customer Order Entry"}
        size="xl"
        centered
        closeOnEscape={false}
        withinPortal={true} // default, but explicit here
        zIndex={2000} // ensure it's above layout elements
      >
        <AdminOrderEntryPage
          recordId={recordId}
          onSaveComplete={(close: boolean) => {
            setReloadTrigger(new Date());
            if (close) setEntryModalOpen(false);
          }}
        />
      </Modal>
      <Modal
        opened={preview}
        onClose={() => setPreview(false)}
        fullScreen
        title="Invoice Preview"
        size="xl"
        centered
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        {recordId && (
          <AdminOrderPreview
            id={recordId}
            orderStage={orderStage}
            invoiceTo={null}
            deliverTo={null}
          />
        )}
      </Modal>
      {exportData && (
        <AdminExportInvoicePage
          data={exportData}
          onDownloadComplete={() => setExportData(null)}
        />
      )}
    </Stack>
  );
};

export default AdminOrderPage;
